import { createConsumer } from '@rails/actioncable'
import type { Consumer, Subscription } from '@rails/actioncable'
import { getToken as getInMemoryToken } from '@/lib/tokenHolder'

// Get auth token for WebSocket connection
const getWsAuthToken = (): string | null => {
  // Try in-memory token first
  const inMemoryToken = getInMemoryToken()
  if (inMemoryToken) return inMemoryToken

  // Fallback to localStorage
  try {
    const stored = localStorage.getItem('medhunter-auth')
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed?.state?.token || null
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

// Use VITE_WS_URL in production, proxy path in development
// In dev: /cable -> proxied to ws://localhost:3000/cable by Vite
// In prod: wss://medhunter-api-xxx.herokuapp.com/cable (different origin)
const buildWsUrl = (): string => {
  const wsUrl = import.meta.env.VITE_WS_URL || '/cable'

  // Add JWT token to URL for cross-origin authentication
  const token = getWsAuthToken()
  if (!token) return wsUrl
  const separator = wsUrl.includes('?') ? '&' : '?'
  return `${wsUrl}${separator}token=${encodeURIComponent(token)}`
}

// A URL e uma FUNCAO: o actioncable a chama a cada (re)conexao. O access token vence
// em 1h; com a URL fixa, a reconexao depois de uma queda mandaria o token velho.
// Os tipos (@types/rails__actioncable) so declaram string; o runtime aceita funcao
// (`createWebSocketURL` em @rails/actioncable/src/consumer.js).
const getConsumer = (): Consumer => createConsumer(buildWsUrl as unknown as string)

let consumer: Consumer | null = null

export const getCableConsumer = (): Consumer => {
  if (!consumer) {
    consumer = getConsumer()
  }
  return consumer
}

// Sync Status Message type (inline to avoid export issues)
interface SyncStatusData {
  platform: string
  status: 'syncing' | 'completed' | 'failed'
  message: string
  timestamp: string
  progress?: number
  details?: Record<string, unknown>
}

export const subscribeSyncStatus = (
  onMessage: (data: SyncStatusData) => void,
  onConnected?: () => void,
  onDisconnected?: () => void
): Subscription => {
  const cable = getCableConsumer()

  return cable.subscriptions.create<SyncStatusData>('SyncStatusChannel', {
    received(data) {
      onMessage(data)
    },
    connected() {
      console.log('Connected to SyncStatusChannel')
      onConnected?.()
    },
    disconnected() {
      console.log('Disconnected from SyncStatusChannel')
      onDisconnected?.()
    },
  })
}

// Disconnect consumer (useful for logout)
export const disconnectCable = (): void => {
  if (consumer) {
    consumer.disconnect()
    consumer = null
  }
}

// Reconnect with fresh token (call after login)
export const reconnectCable = (): Consumer => {
  disconnectCable()
  consumer = getConsumer()
  return consumer
}
